
import React from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { Shield, Users, Lock, BarChart3, CheckCircle2 } from 'lucide-react';
import CustomerRbacVisualizer from './CustomerRbacVisualizer';
import { useNavigate } from 'react-router-dom';
import LogConfigurationTable from '../../components/LogConfigurationTable';

const CustomerSecurityPortal: React.FC = () => {
    const { currentUser } = useAppContext();
    const navigate = useNavigate();

    return (
        <div className="space-y-8 p-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="bg-slate-900 -m-6 mb-2 p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                <div className="relative z-10 flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-emerald-600 p-2 rounded-lg">
                                <Shield size={24} className="text-white" />
                            </div>
                            <span className="text-emerald-400 font-bold tracking-wider text-sm uppercase">Team Administration</span>
                        </div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            Security & Access Portal
                        </h1>
                        <p className="text-slate-400 mt-2 max-w-2xl">
                            Manage team roles, permissions, and audit logging for your organization.
                        </p>
                    </div>
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 flex items-center gap-3">
                        <div className="bg-emerald-500/20 p-2 rounded-full">
                            <CheckCircle2 className="text-emerald-400" size={20} />
                        </div>
                        <div>
                            <div className="text-xs text-slate-400 uppercase font-bold">Organization</div>
                            <div className="font-semibold text-white">{currentUser?.organization || 'Unknown Org'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Grid - Matching AutoGain layout */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Main RBAC Visualizer - 3 columns wide */}
                <div className="lg:col-span-3 space-y-6">
                    {/* Team Access Architecture - RBAC Tree */}
                    <div className="h-[700px] bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden relative">
                        <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded border border-slate-200 text-xs font-bold text-slate-500">
                            Team Access Architecture
                        </div>
                        <CustomerRbacVisualizer />
                    </div>

                    {/* Log Configuration */}
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                            <h3 className="font-bold text-slate-800">Audit Logging & Subscription Limits</h3>
                        </div>
                        <div className="h-[400px]">
                            <LogConfigurationTable />
                        </div>
                    </div>
                </div>

                {/* Control Panel - Right sidebar */}
                <div className="space-y-6">
                    {/* Team Actions */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Team Controls</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => navigate('/admin/users')}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-medium transition-colors flex items-center gap-3"
                            >
                                <Users size={18} /> Manage Team
                            </button>
                            <button
                                onClick={() => navigate('/admin/permissions')}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-medium transition-colors flex items-center gap-3"
                            >
                                <Lock size={18} /> Role Configuration
                            </button>
                            <button
                                onClick={() => navigate('/admin/analytics')}
                                className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-medium transition-colors flex items-center gap-3"
                            >
                                <BarChart3 size={18} /> User Analytics
                            </button>
                        </div>
                    </div>

                    {/* Security Status */}
                    <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Security Status</h3>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                <span className="text-slate-300">RBAC Enforcement</span>
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-bold">
                                    ACTIVE
                                </span>
                            </div>
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                <span className="text-slate-300">Data Isolation</span>
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-bold">ENABLED</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-slate-300">Active Users</span>
                                <span className="text-white font-mono">-</span>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-800">
                            <p className="text-xs text-center text-slate-500">
                                Contact AutoGain support for security incidents.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerSecurityPortal;
